import { useEffect } from 'react';
import { useToolStore } from '../components/Tool/tool.store';

/**
 * Hook to handle global click events for feature selection.
 * Differentiates between actual clicks and pan/drag operations by tracking
 * mouse movement between mousedown and click events.
 */
export function useHandleClick() {
    useEffect(() => {
        let mouseDownPos: { x: number; y: number } | null = null;
        const CLICK_THRESHOLD = 5; // pixels - movement beyond this is considered a pan/drag

        const handleMouseDown = (e: MouseEvent) => {
            mouseDownPos = { x: e.clientX, y: e.clientY };
        };

        const handleGlobalClick = (e: MouseEvent) => {
            const { activeFeatureId, toggleFeatureSelection } =
                useToolStore.getState();

            if (!activeFeatureId) return;

            // Check if mouse moved significantly between mousedown and click (indicating a drag/pan)
            if (mouseDownPos) {
                const dx = Math.abs(e.clientX - mouseDownPos.x);
                const dy = Math.abs(e.clientY - mouseDownPos.y);

                if (dx > CLICK_THRESHOLD || dy > CLICK_THRESHOLD) {
                    // This was a drag/pan, not a click - ignore it
                    mouseDownPos = null;
                    return;
                }
            }

            toggleFeatureSelection(activeFeatureId);
            mouseDownPos = null;
        };

        window.addEventListener('mousedown', handleMouseDown, {
            capture: true,
        });
        window.addEventListener('click', handleGlobalClick);

        return () => {
            window.removeEventListener('mousedown', handleMouseDown, {
                capture: true,
            });
            window.removeEventListener('click', handleGlobalClick);
        };
    }, []);
}
